import { type ComponentProps, type ReactNode } from 'react'

import { FloatingTooltip } from '@/components/common/Floating'
import { type LinkRoute } from '@/components/common/Link'
import { LoadingMask } from '@/components/common/LoadingMask'
import { cn } from '@/utils/commonUtil'

import { ButtonRoot } from '../ButtonRoot/ButtonRoot'

import styles from './ButtonRadioGroup.module.scss'

export type OptionType<T extends string | number = string> = {
  ariaLabel?: string
  disabled?: boolean
  isSelected?: boolean
  label: ReactNode
  linkInternalProps?: Omit<ComponentProps<typeof LinkRoute>, 'children'>
  popover?: ReactNode
  title?: string
  value?: T
}

type Props<T extends string | number> = {
  allowSelectedLink?: boolean
  as?: 'button' | 'LinkRoute'
  border?: 'none'
  colorScheme?: 'neutral'
  disabled?: boolean
  onSelect?: (value: T) => void
  options: OptionType<T>[]
  showLoadingMask?: boolean
  size?: 'sm'
  textStyle?: 'none' | 'heading'
  width?: 'maxcontent' | 'full'
}

export const ButtonRadioGroup = <T extends string | number>({
  allowSelectedLink,
  as = 'LinkRoute',
  border,
  colorScheme,
  disabled: disabledGlobal,
  onSelect,
  options,
  showLoadingMask,
  size,
  textStyle = 'heading',
  width,
}: Props<T>) => (
  <div className={cn(styles.container, { border, colorScheme, size, styles, textStyle, width })}>
    <ul className={styles.list}>
      {options.map(
        ({ ariaLabel, disabled, isSelected, label, linkInternalProps, popover: popoverContent, title, value }, i) => {
          const sharedProps = {
            'aria-label': ariaLabel,
            className: cn(styles.button, { disabled, isSelected, styles }),
            disabled: (isSelected && !allowSelectedLink) || disabledGlobal || disabled,
            onClick: () => onSelect && !disabledGlobal && !disabled && value !== undefined && onSelect(value),
            title,
          }

          return (
            <li className={styles.item} key={i}>
              <FloatingTooltip content={popoverContent}>
                {as === 'LinkRoute' ? (
                  <ButtonRoot as='LinkRoute' {...sharedProps} {...linkInternalProps}>
                    {label}
                  </ButtonRoot>
                ) : (
                  <ButtonRoot {...sharedProps}>{label}</ButtonRoot>
                )}
              </FloatingTooltip>
            </li>
          )
        },
      )}
    </ul>
    <LoadingMask loading={showLoadingMask} />
  </div>
)
